import React, { useEffect, useContext } from "react";
import { Box } from "@mui/material";
import { Home, ChatBubbleOutline, HelpOutline } from "@mui/icons-material";
import { motion, LayoutGroup } from "framer-motion";
import { WidgetContext } from "../../store/widgetContext";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { get } from "lodash";
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';

export default function Navigation({ handleNavigate, currentLocation = "home" }) {
	const { widget } = useContext(WidgetContext);
	const homeEnabled = get(widget, "pages.home.enabled", true);
	const ticketsEnabled = get(widget, "pages.tickets.enabled", false);
	const isFaqsEnabled = get(widget, "pages.faqs.enabled", false);
	const { msg } = useWidgetMsg();

	const items = [
		{ key: "home", label: msg("home.title", "Home"), icon: Home, visible: homeEnabled },
		{ key: "conversations", label: msg("conversations.title", "Conversations"), icon: ChatBubbleOutline, visible: true },
		{ key: "tickets", label: msg("tickets.title", "Tickets"), icon: ConfirmationNumberOutlinedIcon, visible: ticketsEnabled },
		{ key: "articles", label: msg("help.title", "Help"), icon: HelpOutline, visible: isFaqsEnabled },
	];

	const handleNavClick = (location, event) => {
		if (event) event.stopPropagation();
		if (handleNavigate) handleNavigate({ location });
	};

	const visibleItems = items.filter((i) => i.visible);

	useEffect(() => {
		const isCurrentLocationVisible = visibleItems.some((item) => item.key === currentLocation);
		if (!isCurrentLocationVisible && handleNavigate) {
			const firstVisibleLocation = visibleItems.length > 0 ? visibleItems[0].key : "conversations";
			handleNavigate({ location: firstVisibleLocation });
		}
	}, [visibleItems, currentLocation, handleNavigate]);

	return (
		<Box
			sx={{
				position: "absolute",
				left: 10,
				right: 10,
				bottom: 10,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				pointerEvents: "none",
			}}
		>
			<Box
				sx={{
					pointerEvents: "auto",
					width: "100%",
					maxWidth: 520,
					margin: "0 auto",
					padding: "8px 10px",
					borderRadius: "16px",
					background: "rgba(255,255,255,0.24)",
					backdropFilter: "blur(2px) saturate(180%)",
					WebkitBackdropFilter: "blur(2px) saturate(180%)",
					border: "1px solid rgba(255,255,255,0.35)",
					boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
					overflow: "visible",
				}}
			>
				<LayoutGroup>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", gap: '20px' }}>
						{visibleItems.map((item) => {
							const Icon = item.icon;
							const isActive = item.key === currentLocation;

							return (
								<Box
									key={item.key}
									component={motion.button}
									type="button"
									layout="position"
									transition={{ type: "spring", stiffness: 500, damping: 35 }}
									whileHover={!isActive ? { y: -2 } : {}}
									whileTap={{ scale: 0.95 }}
									onClick={(e) => handleNavClick(item.key, e)}
									sx={{
										position: "relative",
										flex: isActive ? "0 1 auto" : "0 0 auto",
										minWidth: 0,
										border: "none",
										outline: "none",
										background: "transparent",
										padding: 0,
										margin: 0,
										cursor: "pointer",
										height: 36,
										borderRadius: 18,
										WebkitTapHighlightColor: "transparent",
									}}
								>
									{isActive && (
										<Box
											component={motion.div}
											layoutId="nav-active-pill"
											initial={{ scale: 0.8 }}
											animate={{ scale: [0.8, 1.1, 1] }}
											transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
											sx={{
												position: "absolute",
												inset: "-2px",
												borderRadius: 18,
												background: "rgba(0,0,0,0.9)",
												boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
											}}
										/>
									)}
									<Box
										sx={{
											position: "relative",
											zIndex: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											height: "100%",
											px: isActive ? 1.2 : 0.4,
											gap: isActive ? 0.5 : 0,
											color: isActive ? "#fff" : "#000",
											whiteSpace: "nowrap",
										}}
									>
										<Box
											component={motion.div}
											animate={{ scale: isActive ? [1, 1.15, 1] : 1 }}
											transition={{ duration: 0.3, ease: "easeOut" }}
											sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
										>
											<Icon
												style={{
													fontSize: 20,
													flexShrink: 0,
													opacity: isActive ? 1 : 0.8,
													transition: "opacity 0.2s ease",
												}}
											/>
										</Box>
										{isActive && (
											<Box
												component={motion.span}
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ duration: 0.2 }}
												sx={{
													fontSize: 13,
													fontWeight: 400,
													lineHeight: 1,
													ml: 0.5,
													whiteSpace: "nowrap",
													display: "inline-block",
												}}
											>
												{item.label}
											</Box>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				</LayoutGroup>
			</Box>
		</Box>
	);
}

